import { useStaticQuery, graphql } from 'gatsby';
import axios from 'axios';
import { getDisplayPrice } from '../utils/priceCalculations';

// Función auxiliar para calcular el monto mínimo basado en las reglas del cupón
const calculateRelevantCartTotal = (coupon, cartItems) => {
  let relevantItems = [...cartItems];

  // Si hay productos incluidos, solo considerar esos productos
  if (coupon.included_products && coupon.included_products.length > 0) {
    relevantItems = cartItems.filter(item => 
      coupon.included_products.some(included => String(included.id) === String(item.product_id))
    );
  }
  // Si hay categorías incluidas, solo considerar productos de esas categorías
  else if (coupon.included_categories && coupon.included_categories.length > 0) {
    relevantItems = cartItems.filter(item => 
      item.categories && item.categories.some(cat => 
        coupon.included_categories.some(included => String(included.id) === String(cat.id))
      )
    );
  }

  // Excluir productos excluidos
  if (coupon.excluded_products && coupon.excluded_products.length > 0) {
    relevantItems = relevantItems.filter(item => 
      !coupon.excluded_products.some(excluded => String(excluded.id) === String(item.product_id))
    );
  }

  // Excluir categorías excluidas
  if (coupon.excluded_categories && coupon.excluded_categories.length > 0) {
    relevantItems = relevantItems.filter(item => 
      !item.categories || !item.categories.some(cat => 
        coupon.excluded_categories.some(excluded => String(excluded.id) === String(cat.id))
      )
    );
  }

  // Calcular el total de los productos relevantes
  const relevantTotal = relevantItems.reduce((total, item) => {
    const price = getDisplayPrice(item);
    return total + (Number(price) * item.quantity);
  }, 0);

  return relevantTotal;
};

// Función auxiliar para verificar cupones válidos
const getValidCoupon = (coupons, cartTotal = 0, cartItems = []) => {
  const now = new Date();
  
  return coupons.find(coupon => {
    // Verificar si el cupón está activo y tiene activación automática
    if (coupon.active !== 1 || coupon.automatic_activation !== 1) {
      return false;
    }

    // Verificar fechas de validez
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);
    if (now < validFrom || now > validUntil) {
      return false;
    }

    // Verificar monto mínimo basado en las reglas del cupón
    const relevantCartTotal = calculateRelevantCartTotal(coupon, cartItems);
    if (relevantCartTotal < coupon.minimun_amount) {
      return false;
    }

    // Verificar límites de uso
    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      return false;
    }

    // Verificar productos incluidos - Ahora solo necesita que al menos uno esté en el carrito
    if (coupon.included_products && coupon.included_products.length > 0) {
      const hasIncludedProduct = cartItems.some(item => 
        coupon.included_products.some(included => String(included.id) === String(item.product_id))
      );
      if (!hasIncludedProduct) {
        return false;
      }
    }

    // Verificar productos excluidos - Si hay alguno excluido, el cupón no se aplica
    if (coupon.excluded_products && coupon.excluded_products.length > 0) {
      const hasExcludedProduct = cartItems.some(item => 
        coupon.excluded_products.some(excluded => String(excluded.id) === String(item.product_id))
      );
      if (hasExcludedProduct) {
        return false;
      }
    }

    // Verificar categorías incluidas - Ahora solo necesita que al menos una esté en el carrito
    if (coupon.included_categories && coupon.included_categories.length > 0) {
      const hasIncludedCategory = cartItems.some(item => 
        item.categories && item.categories.some(cat => 
          coupon.included_categories.some(included => String(included.id) === String(cat.id))
        )
      );
      if (!hasIncludedCategory) {
        return false;
      }
    }

    // Verificar categorías excluidas - Si hay alguna excluida, el cupón no se aplica
    if (coupon.excluded_categories && coupon.excluded_categories.length > 0) {
      const hasExcludedCategory = cartItems.some(item => 
        item.categories && item.categories.some(cat => 
          coupon.excluded_categories.some(excluded => String(excluded.id) === String(cat.id))
        )
      );
      if (hasExcludedCategory) {
        return false;
      }
    }

    return true;
  });
};

// Hook para componentes React
export const useAutomaticCoupon = () => {
  const data = useStaticQuery(graphql`
    query AutomaticCouponsQuery {
      allAutomaticCoupon {
        nodes {
          id
          code
          description
          discount_type
          discount_amount
          valid_from
          valid_until
          usage_limit
          usage_limit_per_user
          usage_count
          used_at
          active
          user_id
          created_at
          updated_at
          deleted_at
          free_delivery
          automatic_activation
          id_verification
          minimun_amount
          included_products {
            id
            name
            slug
            price
            sale_price
          }
          excluded_products {
            id
            name
            slug
            price
            sale_price
          }
          included_categories {
            id
            name
            slug
          }
          excluded_categories {
            id
            name
            slug
          }
        }
      }
    }
  `);

  const validateAndRemoveCoupon = (cartItems = [], cartTotal = 0) => {
    try {
      const storedCouponData = JSON.parse(localStorage.getItem("couponData"));
      if (!storedCouponData) {
        console.log('ℹ️ No hay cupón almacenado para validar');
        return null;
      }

      console.log('🔍 Validando cupón almacenado:', storedCouponData.code);

      // Solo validar cupones automáticos
      if (storedCouponData.isManual === true) {
        console.log('ℹ️ Cupón manual detectado, no se valida automáticamente');
        return true;
      }

      const validCoupon = getValidCoupon(data.allAutomaticCoupon.nodes, cartTotal, cartItems);
      
      if (!validCoupon || validCoupon.code !== storedCouponData.code) {
        console.log('❌ Cupón automático ya no es válido, removiendo...');
        localStorage.removeItem("couponData");
        return false;
      }

      console.log('✅ Cupón automático válido');
      return true;
    } catch (error) {
      console.error('❌ Error al validar cupón:', error);
      localStorage.removeItem("couponData");
      return false;
    }
  };

  const applyAutomaticCoupon = async (cartItems = [], cartTotal = 0) => {
    try {
      const validCoupon = getValidCoupon(data.allAutomaticCoupon.nodes, cartTotal, cartItems);
      
      if (!validCoupon) {
        return null;
      }
      
      // Calcular el monto relevante para el backend
      const relevantCartTotal = calculateRelevantCartTotal(validCoupon, cartItems);
      
      // Verificar el cupón con el backend
      const response = await axios.post(process.env.BASE_URL + "/check-coupon", {
        code: validCoupon.code,
        user_id: null, // Para cupones automáticos no necesitamos user_id
        cart_total: relevantCartTotal, // Usar el monto relevante en lugar del total del carrito
        products: cartItems.map(item => item.product_id),
        categories: [],
      });

      const { message, discount } = response.data;
      
      // Guardar el cupón en localStorage
      const couponData = {
        code: validCoupon.code,
        amount: discount,
        isManual: false // Marcar como cupón automático
      };
      localStorage.setItem("couponData", JSON.stringify(couponData));

      return couponData;
    } catch (error) {
      console.error('❌ Error al aplicar cupón automático:', error);
      return null;
    }
  };

  const applyAutomaticCouponImmediate = (cartItems = [], cartTotal = 0, skipBackendValidation = false) => {
    try {
      // Verificar si ya hay un cupón aplicado
      const existingCoupon = JSON.parse(localStorage.getItem("couponData"));
      if (existingCoupon) {
        // Si es un cupón manual, no aplicar automático
        if (existingCoupon.isManual === true) {
          return existingCoupon;
        }
        // Si es un cupón automático, validar si sigue siendo válido
        const isValid = getValidCoupon(data.allAutomaticCoupon.nodes, cartTotal, cartItems);
        if (isValid && isValid.code === existingCoupon.code) {
          return existingCoupon;
        }
      }
      
      const validCoupon = getValidCoupon(data.allAutomaticCoupon.nodes, cartTotal, cartItems);
      
      if (!validCoupon) {
        // Si no hay cupón válido y había uno automático, removerlo
        if (existingCoupon && !existingCoupon.isManual) {
          localStorage.removeItem("couponData");
        }
        return null;
      }

      // Calcular el monto relevante para el descuento
      const relevantCartTotal = calculateRelevantCartTotal(validCoupon, cartItems);

      // Calcular el descuento inmediatamente basado en el tipo de descuento y el monto relevante
      let discount = 0;
      if (validCoupon.discount_type === 'percentage') {
        discount = (relevantCartTotal * validCoupon.discount_amount) / 100;
      } else if (validCoupon.discount_type === 'fixed') {
        discount = validCoupon.discount_amount;
      }

      // Guardar el cupón en localStorage inmediatamente
      const couponData = {
        code: validCoupon.code,
        amount: discount,
        isManual: false // Marcar como cupón automático
      };
      localStorage.setItem("couponData", JSON.stringify(couponData));

      // Validar con el backend en segundo plano (sin bloquear la UI) solo si no se omite
      if (!skipBackendValidation) {
        validateWithBackend(validCoupon, relevantCartTotal, cartItems, couponData);
      }

      return couponData;
    } catch (error) {
      console.error('❌ Error al aplicar cupón automático inmediatamente:', error);
      return null;
    }
  };

  const validateWithBackend = async (validCoupon, cartTotal, cartItems, couponData) => {
    try {
      // Calcular el monto relevante para el backend
      const relevantCartTotal = calculateRelevantCartTotal(validCoupon, cartItems);
      
      const response = await axios.post(process.env.BASE_URL + "/check-coupon", {
        code: validCoupon.code,
        user_id: null,
        cart_total: relevantCartTotal, // Usar el monto relevante
        products: cartItems.map(item => item.product_id),
        categories: [],
      });

      const { message, discount } = response.data;
      
      // Actualizar el descuento si es diferente
      if (discount !== couponData.amount) {
        // Solo actualizar si la diferencia es significativa (más del 10%)
        const diferenciaPorcentual = Math.abs((discount - couponData.amount) / couponData.amount * 100);
        if (diferenciaPorcentual > 10) {
          const updatedCouponData = {
            ...couponData,
            amount: discount
          };
          localStorage.setItem("couponData", JSON.stringify(updatedCouponData));
        }
      }
    } catch (error) {
      console.error('❌ Error al validar con backend:', error);
      // Si hay error en el backend, remover el cupón
      localStorage.removeItem("couponData");
    }
  };

  const applyAutomaticCouponFrontendOnly = (cartItems = [], cartTotal = 0) => {
    return applyAutomaticCouponImmediate(cartItems, cartTotal, true);
  };

  return {
    getValidCoupon: (cartTotal, cartItems) => getValidCoupon(data.allAutomaticCoupon.nodes, cartTotal, cartItems),
    applyAutomaticCoupon,
    validateAndRemoveCoupon,
    applyAutomaticCouponImmediate,
    applyAutomaticCouponFrontendOnly,
    allCoupons: data.allAutomaticCoupon.nodes
  };
}; 